import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import LivePanel from '../components/LivePanel';
import Kicker from '../components/Kicker';
import ListRow from '../components/ListRow';
import CategoryChip from '../components/CategoryChip';
import ErrorState from '../components/system/ErrorState';
import EmptyState from '../components/system/EmptyState';
import Skeleton from '../components/system/Skeleton';
import { useMyLocation } from '../lib/useMyLocation';
import { useNowTick } from '../lib/useNowTick';
import { usePoll } from '../lib/usePoll';
import { titleCase } from '../lib/titleCase';
import { shortenAircraftType } from '../lib/aircraftType';
import { formatDuration } from '../lib/rap-sheet-format';
import StoryCards from './StoryCards';
import './discover.css';

// react-leaflet/leaflet (and the map-adjacent helpers it pulls in — dead
// reckoning, live trails, the helicopter icon) are heavy and only needed
// once the map actually renders. Loading HeatmapMap lazily keeps all of
// that out of the entry chunk; the headline + leaderboards paint first and
// the map streams in behind its own Suspense boundary.
const HeatmapMap = lazy(() => import('./HeatmapMap'));

function aircraftDisplayName(a) {
  if (a.operatorSource === 'curated' && a.operator) return a.operator;
  if (a.operatorSource === 'raw' && a.operator) return titleCase(a.operator);
  if (a.operator) return a.operator;
  return a.registration || a.icao24;
}

function aircraftSubLine(a) {
  return [
    a.registration,
    shortenAircraftType(a.model),
    a.topNeighborhood?.name,
  ]
    .filter(Boolean)
    .join(' · ');
}

function hoverStatsLine(a) {
  const time = formatDuration(a.hoverTimeS || 0);
  const count = a.eventCount || 0;
  return `${time} · ${count} ${count === 1 ? 'hover' : 'hovers'}`;
}

const LIVE_WITHIN_S = 180;

function Discover({ api, scope = null }) {
  const [aircraftEntries, setAircraftEntries] = useState([]);
  const [regionEntries, setRegionEntries] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [liveAircraft, setLiveAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [lastPolledAt, setLastPolledAt] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const { location, refresh } = useMyLocation();
  const now = useNowTick(1000);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.getAircraftLeaderboard({ days: 7, geohash: scope, limit: 10 }),
      api.getRegionLeaderboard({ days: 7, geohash: scope, limit: 10 }),
    ])
      .then(([a, r]) => {
        if (cancelled) return;
        setAircraftEntries(a.aircraft);
        setRegionEntries(r.regions);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[discover] leaderboard fetch', e);
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, scope, retryNonce]);

  // Hoisted live polling — shared by LivePanel ticker and HeatmapMap
  // (markers). One poll, two consumers.
  const pollLive = useCallback(async (isStale = () => false) => {
    try {
      const [hovers, live] = await Promise.all([
        api.getActiveHovers({
          withinSeconds: 600,
          geohash: scope || undefined,
        }),
        api
          .getLiveAircraft({
            withinSeconds: LIVE_WITHIN_S,
            geohash: scope || undefined,
          })
          .catch(() => ({ aircraft: [] })),
      ]);
      if (isStale()) return;
      setActiveEvents(hovers.events);
      setLiveAircraft(live.aircraft || []);
      setLastPolledAt(Date.now() / 1000);
    } catch (e) {
      console.error('[discover] live poll', e);
    }
  }, [api, scope]);
  usePoll(pollLive);

  const onMyMarkerRefresh = useCallback(() => refresh(), [refresh]);

  const tickerRows = [
    ...activeEvents.map((e) => ({
      icao24: e.icao24,
      label: e.aircraftLabel || e.callsign || e.icao24,
      region: e.regionName || e.geohash5,
      altFt: Number.isFinite(e.meanAltitudeM) ? Math.round(e.meanAltitudeM * 3.28084) : null,
      eventType: e.eventType,
      timeLabel: e.startLocalTime || '',
      geohash5: e.geohash5,
    })),
    ...liveAircraft
      .filter((a) => !activeEvents.some((e) => e.icao24 === a.icao24))
      .slice(0, 8)
      .map((a) => ({
        icao24: a.icao24,
        label: a.registration || a.callsign || a.icao24,
        region: a.regionName || '—',
        altFt: Number.isFinite(a.baro_altitude) ? Math.round(a.baro_altitude * 3.28084) : null,
        eventType: 'flying',
        timeLabel: a.lastSeenLocal || '',
        geohash5: a.geohash5,
      })),
  ].slice(0, 12);

  const livePanelData = {
    hoveringCount: activeEvents.length,
    airborneCount: liveAircraft.length,
    ticker: tickerRows,
    source: 'ADS-B via airplanes.live',
    refreshedSecondsAgo: lastPolledAt != null ? Math.max(0, Math.round(now - lastPolledAt)) : null,
    locationSet: !!location,
  };

  return (
    <div className="discover-body">
      <LivePanel
        data={livePanelData}
        highlightId={highlightId}
        onHighlight={setHighlightId}
        mapSlot={
          <Suspense fallback={<Skeleton blocks={['map']} label="loading map" />}>
            <HeatmapMap
              api={api}
              scope={scope}
              aircraftEntries={aircraftEntries}
              activeEvents={activeEvents}
              liveAircraft={liveAircraft}
              myLocation={location}
              onMyMarkerRefresh={onMyMarkerRefresh}
              highlightId={highlightId}
              onHighlight={setHighlightId}
            />
          </Suspense>
        }
      />

      {error && (
        <ErrorState
          title="Couldn't load leaderboards"
          detail="The recent-activity board didn't come back. Check your connection and try again."
          onRetry={() => setRetryNonce((n) => n + 1)}
        />
      )}

      {!error && loading && (
        <Skeleton blocks={['chip', 'stats', 'block']} label="loading leaderboards" />
      )}

      {!error && !loading && (
        <section className="discover-archive">
          <Kicker>RECENT · 7 DAYS</Kicker>
          {aircraftEntries.length === 0 && regionEntries.length === 0 ? (
            <EmptyState
              title="Nothing on the board yet"
              detail="No hovers logged in this scope over the last 7 days."
            />
          ) : (
            <div className="discover-leaderboards">
              <div>
                <h2 className="discover-list-h2">Worst aircraft</h2>
                <div className="discover-list">
                  {aircraftEntries.length === 0 ? (
                    <EmptyState kicker="AIRCRAFT" title="No repeat offenders yet" />
                  ) : (
                    aircraftEntries.map((a, i) => (
                      <ListRow
                        key={a.icao24}
                        rank={i + 1}
                        chip={<CategoryChip category={a.primaryCategory || 'unknown'} />}
                        title={aircraftDisplayName(a)}
                        sub={aircraftSubLine(a) || null}
                        meta={hoverStatsLine(a)}
                        href={`/a/${a.icao24}`}
                      />
                    ))
                  )}
                </div>
              </div>
              <div>
                <h2 className="discover-list-h2">Most-hovered areas</h2>
                <div className="discover-list">
                  {regionEntries.length === 0 ? (
                    <EmptyState kicker="REGIONS" title="No hovered areas yet" />
                  ) : (
                    regionEntries.map((r, i) => {
                      const count = r.eventCount || 0;
                      const time = formatDuration(r.hoverTimeS || 0);
                      return (
                        <ListRow
                          key={r.geohash5}
                          rank={i + 1}
                          title={r.label || r.geohash5}
                          sub={r.uniqueAircraft ? `${r.uniqueAircraft} aircraft` : null}
                          meta={`${time} · ${count} ${count === 1 ? 'hover' : 'hovers'}`}
                          href={r.slug ? `/n/${r.slug}` : `/r/${r.geohash5}`}
                        />
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          <StoryCards aircraftEntries={aircraftEntries} regionEntries={regionEntries} />
        </section>
      )}
    </div>
  );
}

export default Discover;
